var _effectorFileName = "/src/features/quiz/quiz.tsx";
import React, { useState } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { cn } from '@/shared/lib/cn';
import { QuizResultHeader } from './components/quiz-result';
import { QuizResultContent } from './components/quiz-result-content';
import { QuizResult } from './components/quiz-result-header';
import { QuizProvider } from './quiz-context';
import { QuizQuestion } from './types';
interface QuizProps {
  className?: string;
  questions: QuizQuestion[];
  children?: React.ReactNode;
  contactFormSlot?: React.ReactNode;
  onAnswersChange?: (answers: (number | string)[]) => void;
}
export const Quiz = (props: QuizProps) => {
  const {
    className,
    questions,
    children,
    contactFormSlot,
    onAnswersChange
  } = props;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | string)[]>([]);
  const [isQuizFinished, setIsQuizFinished] = useState(false);
  const [currentStep, setCurrentStep] = useState<0 | 1 | 2>(0);
  const handleAnswerPick = (value: string) => {
    const index = question.answers.findIndex(answer => answer === value);
    setSelectedAnswer(index);
  };
  const handleNextQuestion = () => {
    const currentAnswer = selectedAnswer !== null ? selectedAnswer : '';
    const newAnswers = [...answers, currentAnswer];
    setAnswers(newAnswers);
    onAnswersChange?.(newAnswers);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setIsQuizFinished(true);
      // Если есть слот для контактной формы, переходим к нему, иначе сразу к результатам
      setCurrentStep(contactFormSlot ? 1 : 2);
    }
  };
  const handleContactSubmit = () => {
    setCurrentStep(2);
  };
  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canProceed = selectedAnswer !== null;
  return <QuizProvider onContactFormSubmit={handleContactSubmit}>
      <Card className={cn('flex flex-col place-content-center pb-0 pt-0', className, {
      'pt-4': currentStep === 1 || currentStep === 2
    })}>
        {currentStep === 0 && <CardHeader>
            <CardTitle className="mb-0 text-center text-base font-medium sm:mb-4 sm:text-lg md:text-xl">
              {question.question}
            </CardTitle>
          </CardHeader>}
        <CardContent>
          <AnimatePresence mode="wait">
            {currentStep === 0 && <motion.div key="quiz-content" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}>
                <div>
                  <RadioGroup value={selectedAnswer !== null ? question.answers[selectedAnswer] : ''} onValueChange={handleAnswerPick} className="md:space-y-1">
                    {question.answers.map((answer, index) => <div key={index} className="flex items-center space-x-3 md:space-x-4">
                        <RadioGroupItem value={answer} id={answer} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />

                        <Label className="text-sm md:text-base" htmlFor={answer} dangerouslySetInnerHTML={{
                    __html: answer
                  }} />
                      </div>)}
                  </RadioGroup>
                </div>

                <div className="mt-6 flex items-center justify-center">
                  <Button variant="cta" disabled={!canProceed || isQuizFinished} onClick={handleNextQuestion}>
                    {isLastQuestion ? 'Завершить квиз' : 'Следующий вопрос'}
                  </Button>
                </div>
              </motion.div>}

            {currentStep === 1 && <motion.div key="contact-form" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}>
                <Slot>{contactFormSlot}</Slot>
              </motion.div>}

            {currentStep === 2 && <Slot>{children}</Slot>}
          </AnimatePresence>
        </CardContent>
      </Card>
    </QuizProvider>;
};
export { QuizResult, QuizResultHeader, QuizResultContent };